import { NgModule } from '@angular/core';
import { RouterModule, Routes, mapToCanActivate } from '@angular/router';

import { DefaultLayoutComponent } from './components/layouts/default-layout/default-layout.component';
import { FullPageLayoutComponent } from './components/layouts/full-page-layout/full-page-layout.component';
import { TopbarLayoutComponent } from './components/layouts/topbar-layout/topbar-layout.component';
import { AuthGuard } from './core/security/guards/auth.guard';
import { GeneralSettingsComponent } from './views/account/general-settings/general-settings.component';
import { MyProfileComponent } from './views/account/my-profile/my-profile.component';
import { LayoutDefaultSettingsComponent } from './views/account/settings/layout-default/layout-default-settings.component';
import { PermissionSettingsComponent } from './views/account/settings/permission/permission-settings.component';
import { GeneralMeetingOverviewComponent } from './views/general-meeting-overview/general-meeting-overview.component';
import { GeneralMeetingRegistrationPrintComponent } from './views/general-meeting-registration-print/general-meeting-registration-print.component';
import { GeneralMeetingComponent } from './views/general-meeting/general-meeting.component';
import { GmGateComponent } from './views/general-meeting/gm-gate/gm-gate.component';
import { GmShareholdersViewComponent } from './views/general-meeting/gm-shareholders/gm-shareholders-view/gm-shareholders-view.component';
import { GmShareholdersComponent } from './views/general-meeting/gm-shareholders/gm-shareholders.component';
import { HomeComponent } from './views/home.component';
import { NotificationSettingsComponent } from './views/notification-settings/notification-settings.component';
import { NotificationsComponent } from './views/notifications/notifications.component';
import { ReportsFormComponent } from './views/reports/form/reports-form.component';
import { ReportsComponent } from './views/reports/reports.component';
import { ReportsViewComponent } from './views/reports/view/reports-view.component';
import { SearchComponent } from './views/search/search.component';
import { LoginComponent } from './views/security/login/login.component';
import { ShareholdersFormComponent } from './views/shareholders/form/shareholders-form.component';
import { ShareholdersComponent } from './views/shareholders/shareholders.component';
import { ShareholdersViewComponent } from './views/shareholders/view/shareholders-view.component';
import { TradersFormComponent } from './views/traders/form/traders-form.component';
import { TradersComponent } from './views/traders/traders.component';
import { TradersViewComponent } from './views/traders/view/traders-view.component';
import { TransactionsComponent } from './views/transactions/transactions.component';
import { TransactionsViewComponent } from './views/transactions/view/transactions-view.component';
import { UsersFormComponent } from './views/users/form/users-form.component';
import { UsersComponent } from './views/users/users.component';
import { UsersViewComponent } from './views/users/view/users-view.component';

export const routes: Routes = [
    // Main redirect
    { path: '', redirectTo: 'home', pathMatch: 'full' },

    // Full screen views with layout
    {
        path: '',
        component: FullPageLayoutComponent,
        children: [
            { path: 'login', component: LoginComponent },
            { path: 'gate', component: GmGateComponent },
            {
                path: 'general-meeting-registration-print',
                component: GeneralMeetingRegistrationPrintComponent,
                canActivate: mapToCanActivate([AuthGuard])
            }
        ]
    },

    // Views with topbar
    {
        path: '',
        canActivate: mapToCanActivate([AuthGuard]),
        component: TopbarLayoutComponent,
        children: [{ path: 'home-topbar', component: HomeComponent }]
    },

    // Views with default layout
    {
        path: '',
        canActivate: mapToCanActivate([AuthGuard]),
        component: DefaultLayoutComponent,
        data: {
            showChildrenInMenu: true
        },
        children: [
            {
                path: 'home',
                component: HomeComponent,
                pathMatch: 'full',
                data: {
                    showInMenu: true,
                    title: 'Dashboard',
                    icon: 'dashboard',
                    commandNames: [
                        'GetTopNShareholdersCommand',
                        'GetTopNTransactionsCommand',
                        'GetTotalDispoCommand',
                        'GetTotalRegisteredSharesCommand',
                        'GetShareholderCountCommand',
                        'GetMyFavouritesCommand',
                        'ListTransactionsCommand',
                        'GetFileSharesCommand'
                    ]
                }
            },
            {
                path: 'account',
                children: [
                    {
                        path: 'my-profile',
                        component: MyProfileComponent,
                        data: {
                            title: 'MyProfile'
                        }
                    }
                ]
            },
            {
                path: 'shareholders',
                data: {
                    showChildrenInMenu: true,
                    isSection: true
                },
                children: [
                    {
                        path: '',
                        component: ShareholdersComponent,
                        data: {
                            showInMenu: true,
                            title: 'Shareholders',
                            icon: 'people',
                            commandName: 'ListShareholdersCommand'
                        }
                    },
                    {
                        path: 'add',
                        component: ShareholdersFormComponent,
                        data: {
                            title: 'Add',
                            titleParams: { value: 'MenuItem.Shareholder' },
                            commandName: 'CreateShareholderCommand'
                        }
                    },
                    {
                        path: ':id/edit',
                        component: ShareholdersFormComponent,
                        data: {
                            title: 'Edit',
                            titleParams: { value: 'MenuItem.Shareholder' },
                            commandName: 'UpdateShareholderCommand'
                        }
                    },
                    {
                        path: ':id',
                        redirectTo: ':id/'
                    },
                    {
                        path: ':id/:tab',
                        component: ShareholdersViewComponent,
                        data: {
                            title: 'View',
                            titleParams: { value: 'MenuItem.Shareholder' },
                            commandName: 'GetShareholderSummaryCommand'
                        }
                    }
                ]
            },
            {
                path: 'traders',
                data: {
                    showChildrenInMenu: true,
                    isSection: true
                },
                children: [
                    {
                        path: '',
                        component: TradersComponent,
                        data: {
                            showInMenu: true,
                            title: 'Traders',
                            icon: 'account_balance',
                            commandName: 'ListTradersCommand'
                        }
                    },
                    {
                        path: 'add',
                        component: TradersFormComponent,
                        data: {
                            title: 'Add',
                            titleParams: { value: 'MenuItem.Traders' },
                            commandName: 'CreateTraderCommand'
                        }
                    },
                    {
                        path: ':id/edit',
                        component: TradersFormComponent,
                        data: {
                            title: 'Edit',
                            titleParams: { value: 'MenuItem.Traders' },
                            commandName: 'UpdateTraderCommand'
                        }
                    },
                    {
                        path: ':id',
                        redirectTo: ':id/'
                    },
                    {
                        path: ':id/:tab',
                        component: TradersViewComponent,
                        data: {
                            title: 'View',
                            titleParams: { value: 'MenuItem.Traders' },
                            commandName: 'GetTraderSummaryCommand'
                        }
                    }
                ]
            },
            {
                path: 'transactions',
                data: {
                    showChildrenInMenu: true,
                    isSection: true
                },
                children: [
                    {
                        path: '',
                        component: TransactionsComponent,
                        data: {
                            showInMenu: true,
                            title: 'Transactions',
                            icon: 'sync_alt',
                            commandName: 'ListTransactionsCommand'
                        }
                    },
                    {
                        path: ':id',
                        redirectTo: ':id/'
                    },
                    {
                        path: ':id/:tab',
                        component: TransactionsViewComponent,
                        data: {
                            title: 'View',
                            titleParams: { value: 'MenuItem.Transactions' },
                            commandName: 'GetTransactionSummaryCommand'
                        }
                    }
                ]
            },
            {
                path: 'reports',
                component: ReportsComponent,
                data: {
                    showInMenu: true,
                    title: 'Reports',
                    icon: 'text_snippet',
                    commandName: 'ListReportsCommand'
                },
                children: [
                    {
                        path: 'add',
                        component: ReportsFormComponent,
                        data: {
                            title: 'Add',
                            titleParams: { value: 'MenuItem.Reports' },
                            commandName: 'CreateReportCommand'
                        }
                    },
                    {
                        path: ':id/edit',
                        component: ReportsFormComponent,
                        data: {
                            title: 'Edit',
                            titleParams: { value: 'MenuItem.Reports' },
                            commandName: 'UpdateReportCommand'
                        }
                    },
                    {
                        path: ':id',
                        component: ReportsViewComponent,
                        data: {
                            title: 'View',
                            titleParams: { value: 'MenuItem.Reports' },
                            commandName: 'GetReportCommand'
                        }
                    }
                ]
            },
            {
                path: 'general-meeting',
                data: {
                    showInMenu: true,
                    showAsParentMenu: true,
                    showChildrenInMenu: true,
                    title: 'GeneralMeeting',
                    icon: 'groups',
                    commandNames: ['UpdateGeneralMeetingCommand', 'ListGmShareholdersCommand']
                },
                children: [
                    {
                        path: 'overview',
                        component: GeneralMeetingOverviewComponent,
                        data: {
                            showInMenu: true,
                            showAsSubMenu: true,
                            title: 'GMOverview',
                            commandName: 'UpdateGeneralMeetingCommand'
                        }
                    },
                    {
                        path: 'dashboard',
                        component: GeneralMeetingComponent,
                        data: {
                            showInMenu: true,
                            showAsSubMenu: true,
                            title: 'GMDashboard',
                            commandName: 'UpdateGeneralMeetingCommand'
                        }
                    },
                    {
                        path: 'shareholders',
                        data: {
                            showInMenu: true,
                            showAsSubMenu: true,
                            title: 'GMShareholders',
                            commandName: 'ListGmShareholdersCommand'
                        },
                        children: [
                            {
                                path: '',
                                component: GmShareholdersComponent
                            },
                            {
                                path: ':id',
                                redirectTo: ':id/'
                            },
                            {
                                path: ':id/:tab',
                                component: GmShareholdersViewComponent,
                                data: {
                                    title: 'View',
                                    titleParams: { value: 'MenuItem.Shareholder' },
                                    commandName: 'GetGmShareholderSummaryCommand'
                                }
                            }
                        ]
                    }
                ]
            },
            {
                path: 'settings',
                data: {
                    showInMenu: true,
                    showAsParentMenu: true,
                    showChildrenInMenu: true,
                    title: 'Settings',
                    icon: 'settings',
                    commandNames: ['UpdatePermissionsCommand', 'UpdateBasicDataCommand', 'ListUsersCommand']
                },
                children: [
                    {
                        path: 'general-settings',
                        component: GeneralSettingsComponent,
                        data: {
                            showInMenu: true,
                            showAsSubMenu: true,
                            title: 'GeneralSettings',
                            commandNames: [
                                'UpdateBasicDataCommand' // TODO:
                            ]
                        }
                    },
                    {
                        path: 'default-layout',
                        component: LayoutDefaultSettingsComponent,
                        data: {
                            showInMenu: true,
                            showAsSubMenu: true,
                            title: 'LayoutDefaultSettings',
                            commandNames: ['UpdateBasicDataCommand']
                        }
                    },
                    {
                        path: 'permission',
                        component: PermissionSettingsComponent,
                        data: {
                            showInMenu: true,
                            showAsSubMenu: true,
                            title: 'Permission',
                            commandNames: ['UpdatePermissionsCommand']
                        }
                    },
                    {
                        path: 'users',
                        data: {
                            showInMenu: true,
                            showAsSubMenu: true,
                            title: 'Users',
                            commandName: 'ListUsersCommand'
                        },
                        children: [
                            {
                                path: '',
                                component: UsersComponent,
                                data: {
                                    title: 'Users',
                                    commandName: 'ListUsersCommand'
                                }
                            },
                            {
                                path: 'add',
                                component: UsersFormComponent,
                                data: {
                                    title: 'Add',
                                    titleParams: { value: 'MenuItem.Users' },
                                    commandName: 'CreateUserCommand'
                                }
                            },
                            {
                                path: ':id/edit',
                                component: UsersFormComponent,
                                data: {
                                    title: 'Edit',
                                    titleParams: { value: 'MenuItem.Users' },
                                    commandName: 'UpdateUserCommand'
                                }
                            },
                            {
                                path: ':id',
                                component: UsersViewComponent,
                                data: {
                                    title: 'View',
                                    titleParams: { value: 'MenuItem.Users' },
                                    commandName: 'GetUserCommand'
                                }
                            }
                        ]
                    }
                ]
            },
            {
                path: 'search/:searchTerm',
                component: SearchComponent,
                data: {
                    title: 'Search'
                }
            },
            {
                path: 'notifications',
                component: NotificationsComponent,
                data: {
                    title: 'Notifications'
                }
            },
            {
                path: 'notification-settings',
                component: NotificationSettingsComponent,
                data: {
                    title: 'NotificationSettings'
                }
            }
        ]
    },
    { path: '**', redirectTo: 'home', pathMatch: 'full' }
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule]
})
export class AppRoutingModule {}
